/**
 * Study-group deck context — the platform-neutral React context that
 * carries "you are viewing a deck in study-group mode" state down the
 * render tree.
 *
 * The web flashcards/quiz/memo/PDF screens read this context to gate
 * edit affordances, build return-to-SG links, and pipe owner metadata
 * into avatar tooltips. The RN deck screen will plug into the same
 * context when its SG deck-view lands.
 *
 * `canEdit` mirrors `canEditSGDeck(...)`. `canEditPermissions` mirrors
 * `canEditDeckPermissions(...)`. `isOwner` is the deck-sharer predicate
 * (`addedByUserId === viewer`) — kept as a distinct field so call sites
 * that mean "deck owner specifically" stay legible.
 */
import { createContext, type ReactNode, useContext } from 'react';

export interface StudyGroupContextValue {
  groupId: string;
  deckId: string;
  /** The group folder the deck sits in (null = root) — where Back returns on web. */
  sgFolderId?: string | null;
  groupName: string;
  /**
   * True iff the viewer is the deck's `addedByUserId` — the person
   * who shared it. This is the "deck owner" in the collaboration
   * model: they can always edit, and they're the only one who can
   * change edit permissions (same value as `canEditPermissions`).
   *
   * Existing callers that branch on "am I the deck owner?" continue
   * to read `isOwner` — their intent is "deck owner", not "anyone
   * with write access".
   */
  isOwner: boolean;
  /**
   * True when the viewer has write access to this deck under its
   * current `editPermission` — the deck owner always, plus anyone
   * in `editors[]` when mode is `"specific"`, or any group member
   * when mode is `"everyone"`. Use this to gate the card-editing
   * surface (`restrictedActions` vs. the real actions).
   */
  canEdit: boolean;
  /**
   * True iff the viewer may change who can edit this deck. Alias of
   * `isOwner`; kept as a distinct field so the permissions dialog
   * reads meaningfully at the call site.
   */
  canEditPermissions: boolean;
  isStudyGroupDeck: true;
  onImportRequest?: () => void;
  ownerAvatar?: string | null;
  ownerName?: string | null;
  ownerLastActive?: string | null;
  importOpen?: boolean;
  setImportOpen?: (open: boolean) => void;
  sgKey?: string;
  displayName?: string;
  deckColor?: string;
  ownedDeckKeys?: string[];
  /**
   * SG *folder* study/quiz only: maps each card's `__sg_{gid}_{name}`
   * deckName tag → that deck's human-readable display name. Folder cards
   * are tagged with the mirror key (needed for write-back resolution),
   * which is not a real deckId — so the per-card deck label would
   * otherwise render the raw key. Consumers use this to show the real
   * (renamed) deck name instead.
   */
  deckNameByKey?: Record<string, string>;
  /**
   * SG *folder* study only: maps each card's `__sg_{gid}_{name}` deckName tag
   * → the viewer's local DeckId for that deck — the owner's own deck or a
   * member's mirror. The SAME map the folder's write-backs resolve through
   * (`useLoadSGFolderDecks`). Per-card reads keyed by deck id (a card's
   * document, "View source") must go through it: the owner's deck carries no
   * `__sg_` path, so a path lookup finds nothing for the owner.
   */
  mirrorIdByKey?: Readonly<Record<string, string>>;
}

/**
 * Exported for `materials-permissions.ts` alone, which needs the raw context
 * next to the deck's own link; everything else reads `useStudyGroupContext`.
 */
export const StudyGroupContext = createContext<StudyGroupContextValue | null>(null);

export const useStudyGroupContext = () => useContext(StudyGroupContext);

export const useSGReturnParam = () => {
  const ctx = useContext(StudyGroupContext);
  if (!ctx) return '';
  return `&returnTo=${encodeURIComponent(`/study-groups/${ctx.groupId}/deck/${ctx.deckId}`)}`;
};

export const StudyGroupProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: StudyGroupContextValue;
}) => {
  return (
    <StudyGroupContext.Provider value={value}>{children}</StudyGroupContext.Provider>
  );
};
